import React, { useEffect, useState } from 'react'
import { FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiInfo, FiX } from 'react-icons/fi'

export type SnackbarType = 'success' | 'error' | 'warning' | 'info'
export type SnackbarPosition =
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'
    | 'center'

interface SnackbarProps {
    message: string
    type: SnackbarType
    position: SnackbarPosition
    isVisible: boolean
    onClose: () => void
    duration?: number
    showCloseButton?: boolean
    className?: string
}

const Snackbar: React.FC<SnackbarProps> = ({
    message,
    type,
    position,
    isVisible,
    onClose,
    duration = 5000,
    showCloseButton = true,
    className = ""
}) => {
    const [shouldRender, setShouldRender] = useState(false)
    const [isAnimating, setIsAnimating] = useState(false)

    useEffect(() => {
        if (isVisible) {
            setShouldRender(true)
            // Trigger animation after render
            setTimeout(() => setIsAnimating(true), 10)

            // Auto-close after duration
            if (duration > 0) {
                const timer = setTimeout(() => {
                    handleClose()
                }, duration)
                return () => clearTimeout(timer)
            }
        } else {
            setIsAnimating(false)
            // Wait for animation to complete before unmounting
            setTimeout(() => setShouldRender(false), 300)
        }
    }, [isVisible, duration])

    const handleClose = () => {
        setIsAnimating(false)
        setTimeout(() => {
            onClose()
        }, 300)
    }

    const getTypeConfig = () => {
        switch (type) {
            case 'success':
                return {
                    icon: FiCheckCircle,
                    bgColor: 'bg-emerald-600 dark:bg-emerald-700',
                    borderColor: 'border-emerald-500 dark:border-emerald-600',
                    textColor: 'text-white',
                    iconColor: 'text-emerald-100',
                    shadowColor: 'shadow-emerald-500/20'
                }
            case 'error':
                return {
                    icon: FiAlertCircle,
                    bgColor: 'bg-rose-600 dark:bg-rose-700',
                    borderColor: 'border-rose-500 dark:border-rose-600',
                    textColor: 'text-white',
                    iconColor: 'text-rose-100',
                    shadowColor: 'shadow-rose-500/20'
                }
            case 'warning':
                return {
                    icon: FiAlertTriangle,
                    bgColor: 'bg-amber-600 dark:bg-amber-700',
                    borderColor: 'border-amber-500 dark:border-amber-600',
                    textColor: 'text-white',
                    iconColor: 'text-amber-100',
                    shadowColor: 'shadow-amber-500/20'
                }
            case 'info':
                return {
                    icon: FiInfo,
                    bgColor: 'bg-blue-600 dark:bg-blue-700',
                    borderColor: 'border-blue-500 dark:border-blue-600',
                    textColor: 'text-white',
                    iconColor: 'text-blue-100',
                    shadowColor: 'shadow-blue-500/20'
                }
            default:
                return {
                    icon: FiInfo,
                    bgColor: 'bg-gray-600 dark:bg-gray-700',
                    borderColor: 'border-gray-500 dark:border-gray-600',
                    textColor: 'text-white',
                    iconColor: 'text-gray-100',
                    shadowColor: 'shadow-gray-500/20'
                }
        }
    }

    const getPositionClasses = () => {
        const baseClasses = 'fixed z-[9999] transform transition-all duration-300 ease-in-out'

        switch (position) {
            case 'top-left':
                return `${baseClasses} top-4 left-4 ${isAnimating ? 'translate-x-0 translate-y-0 opacity-100' : '-translate-x-full opacity-0'
                    }`
            case 'top-center':
                return `${baseClasses} top-4 left-1/2 -translate-x-1/2 ${isAnimating ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
                    }`
            case 'top-right':
                return `${baseClasses} top-4 right-4 ${isAnimating ? 'translate-x-0 translate-y-0 opacity-100' : 'translate-x-full opacity-0'
                    }`
            case 'bottom-left':
                return `${baseClasses} bottom-4 left-4 ${isAnimating ? 'translate-x-0 translate-y-0 opacity-100' : '-translate-x-full opacity-0'
                    }`
            case 'bottom-center':
                return `${baseClasses} bottom-4 left-1/2 -translate-x-1/2 ${isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                    }`
            case 'bottom-right':
                return `${baseClasses} bottom-4 right-4 ${isAnimating ? 'translate-x-0 translate-y-0 opacity-100' : 'translate-x-full opacity-0'
                    }`
            case 'center':
                return `${baseClasses} top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                    }`
            default:
                return `${baseClasses} bottom-4 right-4 ${isAnimating ? 'translate-x-0 translate-y-0 opacity-100' : 'translate-x-full opacity-0'
                    }`
        }
    }

    if (!shouldRender) return null

    const typeConfig = getTypeConfig()
    const IconComponent = typeConfig.icon

    return (
        <div className={getPositionClasses()}>
            <div className={`
                max-w-sm w-full
                ${typeConfig.bgColor}
                ${typeConfig.borderColor}
                border-l-4
                rounded-lg
                shadow-lg
                ${typeConfig.shadowColor}
                backdrop-blur-sm
                overflow-hidden
                ${className}
            `}>
                <div className="p-4">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <IconComponent className={`h-5 w-5 ${typeConfig.iconColor}`} />
                        </div>
                        <div className="ml-3 flex-1">
                            <p className={`text-sm font-medium ${typeConfig.textColor} leading-5`}>
                                {message}
                            </p>
                        </div>
                        {showCloseButton && (
                            <div className="ml-4 flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className={`
                                        inline-flex rounded-md
                                        ${typeConfig.textColor}
                                        hover:bg-black/10
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-white/20
                                        focus:ring-offset-2
                                        focus:ring-offset-transparent
                                        transition-colors
                                        duration-200
                                        p-1
                                    `}
                                    aria-label="Close notification"
                                >
                                    <FiX className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Progress bar for auto-dismiss */}
                {duration > 0 && isVisible && (
                    <div className="h-1 bg-black/20 overflow-hidden">
                        <div
                            className="h-full bg-white/30 animate-pulse"
                            style={{
                                width: '100%',
                                transition: `width ${duration}ms linear`,
                                animation: `shrink ${duration}ms linear forwards`
                            }}
                        />
                    </div>
                )}
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                    @keyframes shrink {
                        from { width: 100%; }
                        to { width: 0%; }
                    }
                `
            }} />
        </div>
    )
}

export default Snackbar